import { useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { MonoLabel } from './MonoLabel';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Mono eyebrow above the title. */
  kicker?: string;
  kickerTone?: 'acc' | 'mute' | 'up' | 'down';
  children: ReactNode;
  /** Panel max width; the handoff uses 520, 560 and 620. */
  maxWidth?: 520 | 560 | 620;
}

/** Centre modal on a dimmed backdrop. Closes on Escape and backdrop click. */
export function Modal({ open, onClose, title, kicker, kickerTone = 'acc', children, maxWidth = 560 }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="modal-backdrop"
      onClick={onClose}
      className="modal-backdrop-in fixed inset-0 z-50 flex items-center justify-center bg-[rgba(4,5,8,0.66)] p-8"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth }}
        className={cn(
          'modal-panel-in box-border max-h-[86vh] w-full overflow-auto rounded-lg border border-line bg-surface p-8'
        )}
      >
        <div className="flex items-start justify-between gap-5">
          <div>
            {kicker && (
              <MonoLabel tone={kickerTone} size="md" className="block">
                {kicker}
              </MonoLabel>
            )}
            <h2 id="modal-title" className="mt-[10px] text-[26px] font-semibold tracking-[-0.03em]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-md border border-line2 text-base leading-none text-mute hover:text-ink"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
